import { debug } from "@/lib/debug";

/**
 * Helper WebRTC para Video-Peritación.
 *
 * Diseñado para conexiones móviles inestables y redes hostiles (4G simétrico,
 * WiFi corporativo, hoteles, hospitales). Estrategia:
 *
 *   - bundlePolicy "max-bundle" + rtcpMuxPolicy "require" → menos puertos
 *     UDP necesarios (1 en vez de 4) → más probabilidad de éxito en NAT
 *     estrictos y firewalls.
 *   - iceCandidatePoolSize 4 → empieza a recoger candidatos antes de tener
 *     SDP local, acelera la conexión 1-3 s.
 *   - Auto-restart de ICE si la conexión cae:
 *       • "disconnected" → tras 4 s, restartIce().
 *       • "failed"       → restartIce() inmediato.
 *     Solo el iniciador (perito o el que tenga `isInitiator: true`)
 *     dispara el restart para evitar carreras.
 *   - onState reporta también iceConnectionState para que la UI pueda mostrar
 *     "Reconectando…" en vez de "Conectado" cuando hay glitch.
 */
export function createPeer({
  iceServers,
  sendSignal,
  onTrack,
  onState,
  onRestartNeeded,   // callback opcional para que el caller renegocie con nueva offer
}) {
  const pc = new RTCPeerConnection({
    iceServers: iceServers && iceServers.length > 0 ? iceServers : [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
    ],
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
    iceCandidatePoolSize: 4,
  });

  let disconnectTimer = null;
  const clearDisconnectTimer = () => {
    if (disconnectTimer) { clearTimeout(disconnectTimer); disconnectTimer = null; }
  };

  pc.onicecandidate = (e) => {
    if (e.candidate) {
      sendSignal({ candidate: e.candidate });
    }
  };
  pc.ontrack = (e) => {
    if (e.streams && e.streams[0]) onTrack(e.streams[0]);
  };
  pc.onconnectionstatechange = () => {
    debug("[webrtc] connectionState:", pc.connectionState);
    onState && onState(pc.connectionState, pc.iceConnectionState);
  };
  pc.oniceconnectionstatechange = () => {
    const s = pc.iceConnectionState;
    debug("[webrtc] iceConnectionState:", s);
    onState && onState(pc.connectionState, s);
    if (s === "connected" || s === "completed") {
      clearDisconnectTimer();
    } else if (s === "disconnected") {
      // glitch transitorio: damos 4 s a que se recupere solo antes de reintentar
      clearDisconnectTimer();
      disconnectTimer = setTimeout(() => {
        if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
          debug("[webrtc] ICE stuck disconnected → restartIce()");
          try { pc.restartIce && pc.restartIce(); } catch (_) { /* algunos browsers viejos */ }
          onRestartNeeded && onRestartNeeded();
        }
      }, 4000);
    } else if (s === "failed") {
      debug("[webrtc] ICE failed → restartIce()");
      clearDisconnectTimer();
      try { pc.restartIce && pc.restartIce(); } catch (_) { /* idem */ }
      onRestartNeeded && onRestartNeeded();
    }
  };
  pc.onicegatheringstatechange = () => {
    debug("[webrtc] iceGatheringState:", pc.iceGatheringState);
  };

  return pc;
}

export async function handleSignal(pc, payload, { sendSignal }) {
  if (payload.sdp) {
    const desc = new RTCSessionDescription(payload.sdp);
    await pc.setRemoteDescription(desc);
    debug("[webrtc] setRemoteDescription:", desc.type);
    if (desc.type === "offer") {
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      debug("[webrtc] sending answer");
      sendSignal({ sdp: pc.localDescription });
    }
  } else if (payload.candidate) {
    try {
      await pc.addIceCandidate(payload.candidate);
    } catch (e) {
      console.warn("[webrtc] ICE add error", e);
    }
  }
}
